import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appMonthName } from '@/utils/appDateFormat';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, ArrowRight, Info } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { PageLayout } from '../../../components/ui/PageLayout';
import Button from '../../../components/ui/Button';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import {
  generateSif,
  getSifPreflight,
  getWpsDeadline,
  getKsaWpsHistory,
} from '../../../api/Payroll/wps';
import {
  SifPreflightView,
  WpsDeadlineView,
  KsaWpsSubmissionView,
} from '../../../models/payroll';
import { MudadTimeline } from '../ComplianceCenter/components/MudadTimeline';
import { MarkUploadedModal } from '../ComplianceCenter/components/MarkUploadedModal';
import { KsaMudadHistory } from '../ComplianceCenter/components/KsaMudadHistory';
import { ChecklistButton } from './components/ChecklistButton';
import { CompanySetupCard } from './components/CompanySetupCard';
import { PreflightChecklistDialog } from './components/PreflightChecklistDialog';
import { KsaDeadlineCard } from './components/ksa/KsaDeadlineCard';
import { KsaFileWarningDialog } from './components/ksa/KsaFileWarningDialog';
import { KsaIssueCards } from './components/ksa/KsaIssueCards';
import { KsaReadinessBanners } from './components/ksa/KsaReadinessBanners';
import { KsaSummaryCards } from './components/ksa/KsaSummaryCards';

/** The KSA Mudad screen, moved out of index.tsx unchanged when the UAE screen got its own file record (v1.0.75). */
export const KsaWpsCenter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();
  const country = useComplianceCountry();
  const isKsa = country === 'KSA';

  const today = new Date();
  const [yearMonth, setYearMonth] = useState(today);
  const [ksaHistory, setKsaHistory] = useState<KsaWpsSubmissionView[]>([]);
  const [preflight, setPreflight] = useState<SifPreflightView | null>(null);
  const [deadline, setDeadline] = useState<WpsDeadlineView | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [fileWarningOpen, setFileWarningOpen] = useState(false);
  const [preflightPopupOpen, setPreflightPopupOpen] = useState(false);

  const year = yearMonth.getFullYear();
  const month = yearMonth.getMonth() + 1;
  const monthName = useMemo(
    () => appMonthName(new Date(year, month - 1)),
    [year, month, i18n.language]
  );

  const loadKsaHistory = useCallback(async () => {
    if (!isKsa) return;
    try {
      const resp = await getKsaWpsHistory();
      if (resp.errors.length === 0) setKsaHistory(resp.payload ?? []);
    } catch {
      /* keep prior state */
    }
  }, [isKsa]);

  const loadPeriodInfo = useCallback(async () => {
    setLoading(true);
    try {
      const [pf, dl] = await Promise.all([getSifPreflight(year, month), getWpsDeadline(year, month)]);
      setPreflight(pf.payload || null);
      setDeadline(dl.payload || null);
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load WPS status'), 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month, t, showToast]);

  useEffect(() => { loadKsaHistory(); }, [loadKsaHistory]);
  useEffect(() => { loadPeriodInfo(); }, [loadPeriodInfo]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateSif(year, month);
      showToast(
        isKsa
          ? t('payroll.complianceCenter.ksaWps.generateMudadOk', 'Mudad file downloaded.')
          : t('SIF generated and downloaded'),
        'success'
      );
      if (isKsa) {
        setFileWarningOpen(true);
      }
      await loadKsaHistory();
      await loadPeriodInfo();
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      if (/ksaPayrollMissingForPeriod/i.test(raw)) {
        showToast(
          t(
            'payroll.complianceCenter.ksaWps.noPayrollError',
            'No payroll has been generated for {{month}} {{year}} yet. Create it under Payroll → Monthly Payroll first, then come back to generate the Mudad file.',
            { month: monthName, year },
          ),
          'error',
          12000,
        );
      } else {
        showToast(raw || t('SIF generation failed'), 'error');
      }
    } finally {
      setGenerating(false);
    }
  };
  const latestKsaForPeriod = useMemo(
    () => ksaHistory
      .filter(h => h.year === year && h.month === month)
      .sort((a, b) => +new Date(b.generatedOn) - +new Date(a.generatedOn))[0],
    [ksaHistory, year, month]
  );

  // SifEmployeeIssue fields: userId, employeeName, issue, field, isWarning
  const warningIssues = preflight?.employeeIssues?.filter(e => e.isWarning) ?? [];
  const blockingIssues = preflight?.employeeIssues?.filter(e => !e.isWarning) ?? [];
  const ibanIssues = blockingIssues.filter(e => /iban/i.test(e.issue) || /iban/i.test(e.field)).length;
  const labourCardIssues = blockingIssues.filter(e => /labour|labor|mol/i.test(e.issue + ' ' + e.field)).length;
  const otherIssues = blockingIssues.length - ibanIssues - labourCardIssues;
  const companyChecksFailed = preflight !== null && !preflight.companyPassed;
  const companyFailCount = preflight?.companyChecks?.filter(c => !c.passed).length ?? 0;
  // No payroll generated for the period — generating the file would fail with a raw error.
  const noPayrollForPeriod = preflight !== null && preflight.employeesTotal === 0;

  const title = isKsa ? t('payroll.wps.titleKsa', 'WPS (Mudad)') : t('payroll.wps.titleUae', 'WPS');

  return (
    <PageLayout
      title={title}
      icon={FileSpreadsheet}
      accentColor="emerald"
      maxWidth="full"
      headerActions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" iconName="ArrowLeft">
            <Link to="/payroll/table">{t('Back to Payroll Table')}</Link>
          </Button>
          <Button asChild variant="outline" size="sm" iconName="ExternalLink">
            <Link to="/payroll/compliance-center">{t('Compliance Center')}</Link>
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5" data-testid="wps-center">
        {/* Period selector + summary cards */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <YearMonthPicker date={yearMonth} onChange={setYearMonth} isRtl={false} />
            <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadPeriodInfo} loading={loading}>
              {t('payroll.complianceCenter.wps.recheck', 'Re-run checks')}
            </Button>
            {preflight && <ChecklistButton onClick={() => setPreflightPopupOpen(true)} />}
          </div>
          <KsaSummaryCards
            isKsa={isKsa}
            preflight={preflight}
            deadline={deadline}
            noPayrollForPeriod={noPayrollForPeriod}
            companyChecksFailed={companyChecksFailed}
            companyFailCount={companyFailCount}
          />
        </div>

        {preflight && !noPayrollForPeriod && (
          <KsaReadinessBanners
            isKsa={isKsa}
            blockingCount={blockingIssues.length}
            warningCount={warningIssues.length}
            companyChecksFailed={companyChecksFailed}
            companyFailCount={companyFailCount}
          />
        )}

        <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="wps-regulator-note">
          <span>
            {t('payroll.wps.currency', 'Currency')}: <span className="font-medium">{isKsa ? 'SAR' : 'AED'}</span>
          </span>
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={isKsa ? 'SAMA' : 'CBUAE'}>
                  <Info size={14} />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content className="bg-card border border-border rounded-md p-3 shadow-md text-xs max-w-xs z-50" sideOffset={5}>
                  {isKsa
                    ? t('payroll.wps.samaTooltip', 'Saudi Central Bank (SAMA) requires all salary payments to be denominated in Saudi Riyals (SAR) and processed through licensed banking channels under the Wage Protection System.')
                    : t('payroll.wps.cbuaeTooltip', 'Central Bank of the UAE (CBUAE) requires salary payments in AED through the Wages Protection System.')}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>

        {deadline && <KsaDeadlineCard isKsa={isKsa} deadline={deadline} />}

        {isKsa && deadline && (
          <MudadTimeline currentDay={deadline.daysElapsed} deadlineDays={deadline.deadlineDays} isSubmitted={deadline.isSubmitted} isRunPayrollMode={deadline.isRunPayrollMode ?? false} />
        )}

        {/* Generate panel */}
        <div className="rounded-lg border border-border bg-card p-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-foreground">
              {isKsa
                ? t('payroll.complianceCenter.ksaWps.generateMudad', 'Generate Mudad File')
                : t('Generate SIF for {{month}} {{year}}', { month: monthName, year })}
            </p>
            <p className="text-sm text-muted-foreground">
              {noPayrollForPeriod
                ? t('payroll.complianceCenter.ksaWps.noPayrollHint', 'No payroll exists for {{month}} {{year}} yet. Generate it in the Payroll Table first, then come back here.', { month: monthName, year })
                : preflight?.canGenerate
                ? (warningIssues.length > 0
                    ? t('The file will still generate. Resolve {{n}} coverage warning(s) before the deadline.', { n: warningIssues.length })
                    : t('All employees pass the pre-flight checks for this period.'))
                : t('Resolve {{n}} pre-flight issues before submitting to the bank.', { n: blockingIssues.length })}
            </p>
            {noPayrollForPeriod && (
              <Link to="/payroll/table" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                {t('payroll.complianceCenter.ksaWps.goToPayrollTable', 'Go to Payroll Table')}
                <ArrowRight className="w-3 h-3" />
              </Link>
            )}
            {preflight && !preflight.canGenerate && (
              <ul className="mt-2 text-xs text-muted-foreground space-y-0.5">
                {ibanIssues > 0 && <li>{t('IBAN missing or invalid')}: {ibanIssues}</li>}
                {labourCardIssues > 0 && <li>{t('Labour card missing')}: {labourCardIssues}</li>}
                {otherIssues > 0 && <li>{t('Other issues')}: {otherIssues}</li>}
              </ul>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isKsa && latestKsaForPeriod?.status === 'Generated' && (
              <Button variant="outline" size="sm" onClick={() => setMarkOpen(true)}>
                {t('payroll.complianceCenter.ksaWps.markUploaded', 'Mark Uploaded to Mudad')}
              </Button>
            )}
            <Button
              onClick={handleGenerate}
              disabled={generating || noPayrollForPeriod || (preflight !== null && !preflight.canGenerate)}
              loading={generating}
              iconName="Download"
              data-testid="generate-sif-btn"
            >
              {isKsa
                ? t('payroll.complianceCenter.ksaWps.generateMudad', 'Generate Mudad File')
                : t('Generate SIF')}
            </Button>
          </div>
        </div>

        {preflight && <CompanySetupCard preflight={preflight} />}

        {preflight && <KsaIssueCards warningIssues={warningIssues} blockingIssues={blockingIssues} />}

        {/* History — KSA Mudad table */}
        {isKsa && (
          <div data-testid="wps-history-table">
            <KsaMudadHistory history={ksaHistory} />
          </div>
        )}

        {isKsa && (
          <MarkUploadedModal
            open={markOpen}
            year={year}
            month={month}
            onClose={() => setMarkOpen(false)}
            onSuccess={async () => {
              await loadKsaHistory();
              await loadPeriodInfo();
            }}
          />
        )}

        <PreflightChecklistDialog
          open={preflightPopupOpen}
          onOpenChange={setPreflightPopupOpen}
          preflight={preflight}
          issueLabel={code => t(`payroll.complianceCenter.ksaWps.issue.${code}`, code)}
        />

        <KsaFileWarningDialog open={fileWarningOpen} onOpenChange={setFileWarningOpen} />

        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};
